/**
 * 服务端项目快照请求封装。
 *
 * 用于第五阶段 Editor 同步改造：编辑器打开时从快照恢复 timeline，
 * 保存时把完整 timeline JSON 写入服务端快照。
 */

import { appFetch } from './app-client'
import type {
  ApiResponse,
  PaginatedResponse,
  SnapshotDetail,
  SnapshotSummary,
} from './app-types'

/** 保存快照的入参 */
export interface SaveSnapshotInput {
  snapshotType: 'autosave' | 'manual' | 'published' | 'imported'
  snapshotJson: Record<string, unknown>
  summaryJson?: Record<string, unknown>
}

/**
 * 获取项目最新快照。
 * 返回 null 表示无快照或请求失败（调用方降级到本地）。
 */
export async function fetchLatestSnapshot(
  remoteProjectId: number,
): Promise<SnapshotDetail | null> {
  try {
    const res = await appFetch<ApiResponse<SnapshotDetail>>(
      `/api/projects/${remoteProjectId}/snapshots/latest`,
    )
    return res.data
  } catch (err) {
    // 404 = 无快照，正常降级；其他错误也静默降级
    console.warn('[snapshot] 获取最新快照失败，降级到本地', err)
    return null
  }
}

/**
 * 保存快照到服务端。
 * 失败时只 console.error，不抛错（不阻断本地保存）。
 */
export async function saveSnapshot(
  remoteProjectId: number,
  input: SaveSnapshotInput,
): Promise<SnapshotSummary | null> {
  try {
    const res = await appFetch<ApiResponse<SnapshotSummary>>(
      `/api/projects/${remoteProjectId}/snapshots`,
      {
        method: 'POST',
        body: JSON.stringify({
          snapshotType: input.snapshotType,
          snapshotJson: input.snapshotJson,
          ...(input.summaryJson && { summaryJson: input.summaryJson }),
        }),
      },
    )
    return res.data
  } catch (err) {
    console.error('[snapshot] 保存快照失败，不影响本地保存', err)
    return null
  }
}

/**
 * 获取快照历史列表。
 */
export async function fetchSnapshotList(
  remoteProjectId: number,
  page = 1,
  pageSize = 20,
): Promise<PaginatedResponse<SnapshotSummary> | null> {
  try {
    const res = await appFetch<ApiResponse<PaginatedResponse<SnapshotSummary>>>(
      `/api/projects/${remoteProjectId}/snapshots?page=${page}&pageSize=${pageSize}`,
    )
    return res.data
  } catch (err) {
    console.warn('[snapshot] 获取快照列表失败', err)
    return null
  }
}
